import { useCallback, useMemo, useState } from 'react';
import { useAsyncEffect } from 'react-utils/hooks/useAsyncEffect';
import { Portal } from '../../../common/components/Portal';
import { UIIFrame } from '../../../iframe-wrapper/host';
import { useLifecycleTimers } from '../../hooks/internal';
import { useNavComponentsManager } from '../../hooks/useNavComponentsManager';
import styled from 'styled-components';
import { Z_INDEX_MODAL_COMPONENT } from '../../../constants/z_index';
import { useComponentState } from '../../hooks/useComponentState';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledIframe = styled(UIIFrame).withConfig({
  displayName: "RenderNavComponent__StyledIframe",
  componentId: "sc-15850ru-0"
})(["&.fullscreen-mode{position:fixed !important;top:0px !important;left:0px !important;height:100vh !important;width:100vw !important;z-index:", " !important;background-color:transparent !important;border:none !important;opacity:1 !important;&::before{content:'';display:block;width:100%;height:100%;visibility:hidden;}}"], Z_INDEX_MODAL_COMPONENT);
export const RenderNavComponent = ({
  name,
  ref,
  iframeProps = {},
  src = '',
  path,
  queryParams,
  renderTo,
  scrolling
}) => {
  const {
    handleMessage,
    handleReady,
    getComponentSrc,
    metricsStartTime
  } = useNavComponentsManager();
  const {
    isDisabled,
    isLoaded,
    isOpen,
    isFullscreenMode,
    style
  } = useComponentState(name);

  // Default to `true` except when in synchronous path
  const [isSrcLoading, setIsSrcLoading] = useState(!('jasmine' in window));
  const [componentSrc, setComponentSrc] = useState(src);
  useAsyncEffect(() => {
    if (src || !path) {
      return;
    }
    return getComponentSrc(path, queryParams).then(nextSrc => {
      setComponentSrc(nextSrc);
      setIsSrcLoading(false);
    }).catch(() => {
      setIsSrcLoading(false);
    });
  }, [getComponentSrc, path, queryParams, src]);
  const {
    updateTimerLoaded,
    updateTimerReady
  } = useLifecycleTimers(name);
  const onReady = useCallback(ctx => {
    handleReady(name, ctx);
    updateTimerReady();
  }, [handleReady, name, updateTimerReady]);
  const context = useMemo(() => ({
    componentName: name,
    src,
    path
  }), [name, src, path]);
  const {
    onLoad: onLoadProp
  } = iframeProps;
  const onLoad = useCallback(event => {
    updateTimerLoaded();
    if (onLoadProp) {
      onLoadProp(event);
    }
  }, [onLoadProp, updateTimerLoaded]);
  const computedStyle = useMemo(() => {
    const nativeStyle = {
      display: isOpen ? 'block' : 'none'
    };
    return Object.assign({}, iframeProps.style, nativeStyle, style);
  }, [iframeProps.style, style, isOpen]);
  const iframePassthruProps = useMemo(() => Object.assign({}, iframeProps, {
    style: computedStyle,
    name,
    onLoad
  }), [iframeProps, computedStyle, name, onLoad]);
  if (!isLoaded || isDisabled) {
    return null;
  }

  // If path was provided but component src was not provided yet (async)
  if (typeof path === 'string' && isSrcLoading) {
    return null;
  }
  const embeddedPassthruProps = {
    handshake: {
      metricsStartTime
    }
  };

  // Portal will render in place when renderTo is not defined or returns null
  return /*#__PURE__*/_jsxs(Portal, {
    container: renderTo,
    children: [isFullscreenMode && /*#__PURE__*/_jsx("div", {
      className: "fullscreen-placeholder",
      style: computedStyle,
      "data-test-id": `${name.replace(':', '_')}-fullscreen-placeholder`
    }), /*#__PURE__*/_jsx(StyledIframe, {
      appName: "navigation-components",
      appInfo: context,
      onReady: onReady,
      onMessage: handleMessage,
      onInitError: error => {
        console.error(error, context);
      },
      onReadyError: console.error,
      src: componentSrc,
      iframePassthruProps: iframePassthruProps,
      innerRef: ref,
      readyTimeout: 60000,
      initTimeout: 60000,
      scrolling: scrolling,
      embeddedPassthruProps: embeddedPassthruProps,
      className: isFullscreenMode ? 'fullscreen-mode' : ''
    })]
  });
};